import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Col, Flex, Select, Spin, Table, Tabs, Tooltip} from 'antd';
import {ArrowsCounterClockwise, FloppyDisk} from '@phosphor-icons/react';
import RoofSquareUnavailable from "./RoofSquareUnavailable";
import {useGetSaveDesignData} from "../../../../../../hooks/useGetSaveDesignData";
import {roofSquareService} from "../../../../../../services/roofSquareService";
import {useViewerStore} from "../../../../../../store/store";
import {Toggle} from "../../../../../index";
import { populateRoofSquareData } from '../../../Three-JS-Viewer/Controllers/RoofSquareControl';
import {updateActiveStatusOfRoof} from "../../../../../../helpers/storeHelpers";
import { Panes, PriceKey, RoofMesh, RoofSquareInfo, RoofSquareTableProps, SlopeData } from '../../../../../../@types/types';

export const roundedBasedOn33Percent = (value: number) => {
    // Get the decimal part of the number
    const decimalPart = Number(value - Math.floor(value)).toFixed(2);

    // If the decimal part is greater than or equal to 0.33, return the ceiling
    if (Number(decimalPart) >= 0.33) {
        return Math.ceil(value);
    }
    // Otherwise, return the floor
    else {
        return Math.floor(value);
    }
}

const RoofSquareTable: React.FC<RoofSquareTableProps> = ({isAttachedInSidebar}) => {
    const { sceneViewer, designList, accessoryBuildingList, roofSquareTableData: roofRow, 
        roofSquarePricingData: roundingData,
        activeStatuses, isRoofTagVisible, isRoofSquareDataUnavailable,
        isRoofActiveStatusUpdating, isRoofSquarePriceUpdating, isRoofSquareInitialDataLoaded,
        selectedPricingTier, selectedDesignIndex
    } = useViewerStore();
    const roofsAltered = useViewerStore(state => state.roofsAltered);

    const [tabItems, setTabItems] = useState<Panes[]>([]);
    const getPayloadForSavingRofSquare = useGetSaveDesignData();
    const [isRoofSquareSaveInProgress, setIsRoofSquareSaveInProgress] = useState(false);

    const pricingTierOptions = [
        {
            label: 'Floor',
            value: 'Floor',
        },
        {
            label: 'Min',
            value: 'Min',
        },
        {
            label: 'Standard',
            value: 'Standard',
        },
        {
            label: 'Standard+1',
            value: 'Standard+1',
        },
    ]

    const onChangePricingTier = (value: string) => {
        useViewerStore.setState({selectedPricingTier: value});
        useViewerStore.setState({roofSquarePricingData: {...roundingData}});
    }

    const getSelectedPrice = (selectedPricingTier: string) => {
        switch(selectedPricingTier){
            case "Floor":
                return "floorPrice";
            case "Min":
                return "minPrice";
            case "Standard":
                return "standardPrice";
            default: //Standard + 1
                return "standardPlusOnePrice";
        }
    }

    const columns = [
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            align: 'center' as const,
            render: (_: any, record: RoofSquareInfo) => {
                const status = activeStatuses?.find(item => Number(item.roofId) === Number(record.roofId));

                // If we can't find the status at all (e.g., during loading or sync delay), show a spinner
                if (!status) {
                    return <Spin />;
                }

                // Otherwise show the checkbox based on actual isSelected value
                return (
                    <Checkbox
                        disabled={isRoofActiveStatusUpdating || roofRow
                            .flatMap((b) => b.data)
                            .find((item) => Number(item.roofId) === Number(record.roofId))?.isRoofAccessory}
                        checked={activeStatuses?.find((item) => Number(item.roofId) === Number(record.roofId))?.isSelected}
                        onChange={(e) => {
                            const newStatus = e.target.checked;
                            updateActiveStatusOfRoof(record.roofId, newStatus);
                        }}
                    />
                );
            }
        },
        {title: 'Roof', dataIndex: 'roofId', key: 'roof', align: 'center' as const},
        {
            title: 'Low(<10'+String.fromCharCode(176)+")",
            dataIndex: 'lowSlopeSquare',
            key: 'lowSlopeSquares',
            align: 'center' as const,
            width: '25%',
            render: (_: any, row: RoofSquareInfo) => {
                return {
                    children: (!row.lowSlopeSquare || row.lowSlopeSquare === 0) ? 0 : row.lowSlopeSquare.toFixed(2)
                }
            }
        },
        {
            title: `Normal`,
            dataIndex: 'normalSlopeSquare',
            key: 'normalSlopeSquares',
            align: 'center' as const,
            width: '25%',
            render: (_: any, row: RoofSquareInfo) => {
                return {
                    children: (!row.normalSlopeSquare || row.normalSlopeSquare === 0) ? 0 : row.normalSlopeSquare.toFixed(2)
                }
            }
        },
        {title: 'Total', dataIndex: 'totalRoofSquare', key: 'totalSquares', align: 'center' as const,
            render: (_: any, row: RoofSquareInfo) => {
                //     id text is "total" then make it bold
                return { children: (!row.totalRoofSquare || row.totalRoofSquare === 0) ? 0 : row.totalRoofSquare.toFixed(2) }
            }
        },
    ];

    const RoundingTablecolumns = [
        {title: 'Slope', dataIndex: 'slope', key: 'slope', align: 'center' as const, width: '25%',


            render: (_: any, row: any) => {

                if(row.key === 'total'){
                    return {
                        children: <b>{row.slope}</b>,
                    }
                }
                else {
                    return {
                        children: row.slope
                    }
                }
            },

        }
        ,
        {title: 'Total', dataIndex: 'rounded', key: 'rounded', align: 'center' as const,width: '25%',

            render: (_: any, row: any) => {
                if(row.key === 'total'){
                    return {
                        children: isRoofSquarePriceUpdating? <Spin/> : <b>{row.rounded}</b>,
                    }
                }
                else {
                    return {
                        children: isRoofSquarePriceUpdating ? <Spin/> : row.rounded
                    }
                }
            }

        },

        {
            title: <>
                <Select
                    title={"Select Pricing Tier"}
                    value={selectedPricingTier}
                    placeholder={"Select Pricing Tier"}
                    className={"pricingTierSelect"}
                    options={pricingTierOptions}
                    onChange={onChangePricingTier}
                />
            </>,
            dataIndex: 'price',
            align: 'center' as const,
            key: 'price',
            render: (index: number) => {
                const selectedPrice: PriceKey = getSelectedPrice(selectedPricingTier as string);
                const priceData = roundingData.priceData;

                const obj = {
                    children: (
                        <>
                            {
                                index === 0 ?
                                    <Col className={isAttachedInSidebar ? "roof-square-table-font-size-overwrite" : ''}>
                                        {isRoofSquarePriceUpdating ? <Spin/> : selectedPrice && priceData && `$${priceData.lowSlopePriceResponse[selectedPrice]}`}
                                    </Col>
                                    :index === 1 ?
                                        <Col className={isAttachedInSidebar ? "roof-square-table-font-size-overwrite" : ''}>
                                            {isRoofSquarePriceUpdating ? <Spin/> : selectedPrice && priceData &&`$${priceData.normalSlopePriceResponse[selectedPrice]}`}
                                        </Col>
                                        :
                                        <Col className={isAttachedInSidebar ? "roof-square-table-font-size-overwrite" : ''}>
                                            {isRoofSquarePriceUpdating ? <Spin/> : selectedPrice && priceData && `$${Math.round(Number( priceData.lowSlopePriceResponse[selectedPrice]) + Number(priceData.normalSlopePriceResponse[selectedPrice]))}`}
                                        </Col>


                            }
                        </>
                    ),
                    props: {},
                };


                return obj;

            },
        },

    ];


    const toggleRoofTag = () => {
        sceneViewer.toggleRoofTag(!isRoofTagVisible);
        useViewerStore.setState({isRoofTagVisible: !isRoofTagVisible});
    };


    useEffect(() => {
        const buildingArr = roofRow.map(building => building.buildingName );
        let panes: Panes[] = [];

        if (sceneViewer && activeStatuses?.length && buildingArr.length) {
            panes = buildingArr.map((buildingName): Panes | null => {
                const buildingEntry = roofRow.find(e => e.buildingName === buildingName);
                const buildingData = buildingEntry?.data ?? [];

                if (!buildingData.length || accessoryBuildingList.includes(buildingData[0].buildingId)) {
                    return null;
                }

                return {
                    label: `B${buildingName}`,
                    key: buildingName,
                    children: (
                        <div className={'tab-content-container without-tab'}>
                            <div className={`${!isAttachedInSidebar ? 'popup__inner' : ''}`}>
                                <Flex vertical gap={10}>
                                    <Flex vertical gap={2}>
                                        <span className='popup__subtitle'>Squares Per Roof</span>
                                        <Table
                                            bordered
                                            columns={columns}
                                            dataSource={buildingData}
                                            rowKey={(record) => `B${record.buildingId}-R${record.roofId}`}
                                            rowClassName={(record) => record.isRoofAccessory ? 'row-disabled' : ''}
                                            onRow={(record) => ({
                                                onMouseEnter: () => sceneViewer.highlightRoof(record.roofId, true),
                                                onMouseLeave: () => sceneViewer.highlightRoof(record.roofId, false),
                                            })}
                                            pagination={false}
                                            className='roof_square_table'
                                            scroll={{ y: isAttachedInSidebar ? 200 : 'calc(43vh - 220px)' }}
                                            summary={(pageData) => {
                                                const activeData = pageData.filter(item =>
                                                    activeStatuses?.some(
                                                        statusItem => Number(statusItem.roofId) === Number(item.roofId) && statusItem.isSelected
                                                    )
                                                );

                                                const lowSlopeTotal = roundedBasedOn33Percent(
                                                    activeData.reduce((total, item) => total + Number(item.lowSlopeSquare), 0)
                                                );
                                                const normalSlopeTotal = roundedBasedOn33Percent(
                                                    activeData.reduce((total, item) => total + Number(item.normalSlopeSquare), 0)
                                                );
                                                const totalBuildingTotal = lowSlopeTotal + normalSlopeTotal;

                                                return (
                                                    <Table.Summary fixed>
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell align="center" colSpan={2} index={0}>
                                                                <b>Building Total</b>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="center">
                                                                <b>{lowSlopeTotal}</b>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={2} align="center">
                                                                <b>{normalSlopeTotal}</b>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={3} align="center">
                                                                <b>{totalBuildingTotal}</b>
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell align="center" colSpan={5} index={0}>
                                                                <small>Note: Normal Slope values shown above includes Steep Slope</small>
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    </Table.Summary>
                                                );
                                            }}
                                        />
                                        <span className='popup__subtitle'>Total Site Summary</span>

                                        <Table
                                            locale={{ emptyText: 'Loading...' }}
                                            bordered
                                            columns={RoundingTablecolumns}
                                            dataSource={roundingData.roundingData?.filter(item => item.key !== 'price') || []}
                                            rowKey={(record) => record.key}
                                            pagination={false}
                                            className='roof_square_table'
                                            summary={() => (
                                                <Table.Summary fixed>
                                                    <Table.Summary.Row>
                                                        <Table.Summary.Cell align="center" colSpan={4} index={0}>
                                                            <small>Note: Pricing Shown Displayed based on Cash Financing</small>
                                                        </Table.Summary.Cell>
                                                    </Table.Summary.Row>
                                                </Table.Summary>
                                            )}
                                        />
                                    </Flex>
                                </Flex>
                            </div>
                        </div>
                    ),
                };
            })
            .filter((pane): pane is Panes => pane !== null);
        }

        if (panes.length === 0 && accessoryBuildingList.length > 0 && !isRoofSquareDataUnavailable ){
            useViewerStore.setState({ 
                isRoofSquareDataUnavailable: true,
                isRoofSquarePriceUpdated: true,
                roofSquareErrorMessage: 'No primary buildings available',
            }); 
        }
        
        setTabItems(panes);
    }, [sceneViewer, roofRow, roundingData, activeStatuses, accessoryBuildingList]);

    async function fetchRoundedAndPricingData() {

        if(activeStatuses?.length === 0) return;
        const buildingWiseTotal: Record<number, SlopeData> = {}

        sceneViewer.roofMeshArr.forEach((roof: RoofMesh) => {
            if (roof.flatRoof === true) return;
            if (accessoryBuildingList.includes(roof.userData.buildingName)) {
                // if accessory building then set isRoofSquareActive to false as default and roof square to null
                roof.userData.roofSquare = {
                    isRoofSquareActive: false,
                    lowSquare: null,
                    normalSquare: null,
                }
            } else {
                // if roof.userData.roof_tag_id is not present return
                const isRoofSquareActive = activeStatuses?.find((item) => Number(item.roofId) === Number(roof.userData.roof_tag_id))?.isSelected ?? true;
                roof.userData.roofSquare = { ...roof.userData.roofSquare, isRoofSquareActive };
            }
        });

        roofRow.forEach((building) => {
            building.data.forEach((item) => {
                if(activeStatuses?.find(statusItem => Number(statusItem.roofId) === Number(item.roofId))?.isSelected === true){
                    buildingWiseTotal[item.buildingId] = buildingWiseTotal[item.buildingId] ? {
                        lowSlope: buildingWiseTotal[item.buildingId].lowSlope + Number(item.lowSlopeSquare),
                        normalSlope: buildingWiseTotal[item.buildingId].normalSlope + Number(item.normalSlopeSquare)
                    } : {
                        lowSlope: Number(item.lowSlopeSquare),
                        normalSlope: Number(item.normalSlopeSquare)
                    }
                }
            });
        });

        Object.keys(buildingWiseTotal).forEach((key) => {
            const numKey = Number(key);
            buildingWiseTotal[numKey].lowSlope = (Number(buildingWiseTotal[numKey].lowSlope.toFixed(2)));
            buildingWiseTotal[numKey].normalSlope = (Number(buildingWiseTotal[numKey].normalSlope.toFixed(2)));
        });

        const totalLowSquares = Object.values(buildingWiseTotal).reduce((total, item) => total + roundedBasedOn33Percent(item.lowSlope), 0);
        const totalNormalSquares = Object.values(buildingWiseTotal).reduce((total, item) => total + roundedBasedOn33Percent(item.normalSlope), 0);



        const sumOfLowAndNormalRounded =  Math.ceil(totalLowSquares + totalNormalSquares)


        const excelServiceToken = await roofSquareService.getRoofSquarePricingAPIToken();
        const stateCode = sessionStorage.getItem('stateCode') ?? '';
        const lowSlopePriceResponse = await roofSquareService.getRoofSquarePricingData(
            excelServiceToken,
            stateCode,
            totalLowSquares,
            0,
        );

        const normalSlopePriceResponse = await roofSquareService.getRoofSquarePricingData(
            excelServiceToken,
            stateCode,
            0,
            totalNormalSquares

        );


        return {
            totalLowSquares,
            totalNormalSquares,
            roofSquarePrice: {
                lowSlopePriceResponse : {
                    floorPrice: lowSlopePriceResponse.floorPrice,
                    standardPrice: lowSlopePriceResponse.standardPrice,
                    minPrice: lowSlopePriceResponse.minPrice,
                    standardPlusOnePrice: lowSlopePriceResponse.standardPlusOnePrice
                },
                normalSlopePriceResponse : {
                    floorPrice: normalSlopePriceResponse.floorPrice,
                    standardPrice: normalSlopePriceResponse.standardPrice,
                    minPrice: normalSlopePriceResponse.minPrice,
                    standardPlusOnePrice: normalSlopePriceResponse.standardPlusOnePrice
                }
            },
            sumOfLowAndNormalRounded

        };
    }

    useEffect(() => {

        if(activeStatuses?.length === 0) return;
        if(roofRow.length === 0) return;

        useViewerStore.setState({isRoofSquarePriceUpdating: true});
        useViewerStore.setState({isRoofSquarePriceUpdated: false});
        fetchRoundedAndPricingData().then((result) => {
            if (!result) return;

            const {totalLowSquares, totalNormalSquares, roofSquarePrice, sumOfLowAndNormalRounded} = result;

                const roundingData = {
                    roundingData: [
                        {
                            key: 'total-low',
                            slope: 'Low',
                            rounded: totalLowSquares,
                        },
                        {
                            key: 'total-normal',
                            slope: 'Normal',
                            rounded: totalNormalSquares,
                        },
                        {
                            key: 'total',
                            slope: 'Total',
                            rounded: sumOfLowAndNormalRounded,
                        },
                        {
                            key: 'price',
                            price: roofSquarePrice,
                        },
                    ],
                    priceData: roofSquarePrice,
                }

                useViewerStore.setState({ roofSquarePricingData : roundingData as any ,isRoofSquarePriceUpdating: false, isRoofSquarePriceUpdated: true });
            }
        );
    }, [roofRow, activeStatuses,accessoryBuildingList]);


    const showAlertOnSave = (message: string) => {
        if(message === "Succesfully Updated"){
            sceneViewer.viewerAlert({
                show: true,
                title: "Success",
                message: "Successfully saved Roof Square Data",
                messageType: "success",
                isModal: false,
            });
        }
        else{
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: message,
                messageType: "error",
                isModal: false,
            });
        }

    }

    const saveRoofSquare = async () => {
        setIsRoofSquareSaveInProgress(true);

        try {
            // Update isActive statuses according to checkboxes in table
            const roofRowData = roofRow.map((building) => {
                return {
                    buildingName: building.buildingName,
                    data: building.data.map((roof) => {
                        return {
                            ...roof,
                            isActive: activeStatuses.find((item) => item.roofId === roof.roofId)?.isSelected
                        }
                    })
                }
            })
            useViewerStore.setState({roofSquareTableData: roofRowData as any});

            const payload = await getPayloadForSavingRofSquare();

            const saveResponse = await roofSquareService.saveRoofSquare(payload)
            showAlertOnSave(saveResponse);
        } catch (ex: any) {
            showAlertOnSave(ex);
        } finally {
            setIsRoofSquareSaveInProgress(false);
        }
    }

    const onRefreshRoofSquareData =  () => {
        const selectedDesign = (designList && selectedDesignIndex) ? designList[selectedDesignIndex] : null;
        useViewerStore.setState({  isRoofSquareInitialDataLoaded: false });
        populateRoofSquareData(selectedDesign);
    }

    return (
        <>
            {
                !isRoofSquareInitialDataLoaded ?
                    (isAttachedInSidebar ?
                        <div className='tab__inner'>
                            <div className='tab__column'>
                                <Spin/> <span className={"theme-based-text-color"}>Loading Roof Square Data...</span>
                            </div>
                        </div>
                        : <><Spin/> <span className={"theme-based-text-color"}>Loading Roof Square Data...</span></>)
                    :
                    isAttachedInSidebar ? (
                        <div className='tab__inner'>
                            <div className='tab__title'>
                                <span className='tab__title--text'>Roof Square</span>
                            </div>
                            {isAttachedInSidebar && (
                                <div className='tab__column'>
                                    <div className='tab__flex justify-between'>
                                        <span className='tab__column--subtitle mb-0'>Show/Hide Roof Tag</span>
                                        <Toggle on={isRoofTagVisible} onChange={toggleRoofTag}/>
                                    </div>
                                </div>
                            )}
                            <br/>
                            
                            <>
                                <div className='tab__column'>
                                    <div className={"roof-square-table-action-container sidebar-attached-actions"}>
                                    <Button
                                        size={'small'}
                                        loading={isRoofSquareSaveInProgress}
                                        title={"Re-fetch Roof Square Data"}
                                        icon={<ArrowsCounterClockwise  size={15} />}
                                        className={"roof-square-table-re-fetch-button-sidebar"}
                                        type='primary'
                                        onClick={onRefreshRoofSquareData}
                                    >
                                    </Button>
                                    {!isRoofSquareDataUnavailable && <Tooltip
                                    title={selectedDesignIndex !== 0 ? "Can only save on the most recent design"
                                            : roofsAltered.length > 0 ? "Manually activated/deactivated roof(s) detected, please finalize to save" : ""}
                                    placement="top"
                                >
                                    <span> {/** Needed because Tooltip does not work on disabled buttons */}
                                        <Button
                                            size={'small'}
                                            loading={isRoofSquareSaveInProgress}
                                            title={"Save Roof Square Data"}
                                            icon={<FloppyDisk size={15} />}
                                            className={"roof-square-table-save-button"}
                                            type='primary'
                                            onClick={saveRoofSquare}
                                            disabled={selectedDesignIndex !== 0 || roofsAltered.length > 0}
                                        >
                                            Save
                                        </Button>
                                    </span>
                                </Tooltip>}
                                </div>
                                        {isRoofSquareDataUnavailable
                                            ? <RoofSquareUnavailable />
                                            : <Tabs type={'card'} size={'large'} items={tabItems} />}
                                </div>
                            </>
                        </div>
                    ) : (
                            <>
                            <div className={"roof-square-table-action-container"}>
                                <Button
                                    loading={isRoofSquareSaveInProgress}
                                    title={"Re-fetch Roof Square Data"}
                                    icon={<ArrowsCounterClockwise  size={15} />}
                                    type='primary'
                                    className={"roof-square-table-re-fetch-button"}

                                    onClick={onRefreshRoofSquareData}
                                >
                                </Button>
                                {!isRoofSquareDataUnavailable && <Tooltip
                                    title={selectedDesignIndex !== 0 ? "Can only save on the most recent design"
                                            : roofsAltered.length > 0 ? "Manually activated/deactivated roof(s) detected, please finalize to save" : ""}
                                    placement="top"
                                >
                                    <span> {/** Needed because Tooltip does not work on disabled buttons */}
                                        <Button
                                            loading={isRoofSquareSaveInProgress}
                                            title={"Save Roof Square Data"}
                                            icon={<FloppyDisk size={20} />}
                                            className={"roof-square-table-save-button"}
                                            type='primary'
                                            onClick={saveRoofSquare}
                                            disabled={selectedDesignIndex !== 0 || roofsAltered.length > 0}
                                        >
                                            Save
                                        </Button>
                                    </span>
                                </Tooltip>}
                            </div>
                                {isRoofSquareDataUnavailable
                                    ? <RoofSquareUnavailable />
                                    : <Tabs type={"card"} size={'large'} items={tabItems} />}
                            </>)


            }




        </>
    );
};


export default RoofSquareTable;
